<template>
  <div class="pos-container">
    <!-- LEFT: Menu -->
    <div class="pos-menu">
      <!-- Category Bar -->
      <div class="pos-cats">
        <button class="pos-cat" :class="{active:activeCat===''}" @click="activeCat=''">All</button>
        <button class="pos-cat" :class="{active:activeCat===c.id}" v-for="c in categories" :key="c.id" @click="activeCat=c.id">{{ c.name }}</button>
      </div>

      <!-- Search -->
      <div class="pos-search">
        <input class="st-input" v-model="menuSearch" placeholder="🔍 Search menu... e.g. bread, juice, ugali" style="font-size:14px;padding:12px 16px;" />
      </div>

      <!-- Menu Grid -->
      <div class="pos-grid">
        <div class="pos-item" v-for="item in filteredMenu" :key="item.id" @click="addToCart(item)" :class="{'pos-item-out':!item.is_available||item.is_available==='0'}">
          <div class="pos-item-price">{{ fNum(item.price) }}</div>
          <div class="pos-item-name">{{ item.name }}</div>
          <div class="pos-item-cat" v-if="getCatName(item.category_id)">{{ getCatName(item.category_id) }}</div>
        </div>
        <div v-if="filteredMenu.length===0" class="pos-empty">
          <p>No menu items. Add items in Menu Management.</p>
        </div>
      </div>
    </div>

    <!-- RIGHT: Cart + Payment -->
    <div class="pos-cart-panel">
      <!-- Header -->
      <div class="pos-cart-header">
        <h5 style="margin:0;color:#E8EDF4;font-weight:700;">Order</h5>
        <button class="pos-clear-btn" @click="clearCart" v-if="cart.length">Clear</button>
      </div>

      <!-- Cart Items -->
      <div class="pos-cart-items">
        <div v-if="cart.length===0" class="pos-cart-empty">
          <p>Tap menu items to add to order</p>
        </div>
        <div class="pos-cart-item" v-for="(item,idx) in cart" :key="idx">
          <div class="pos-cart-item-info">
            <div class="pos-cart-item-name">{{ item.name }}</div>
            <div class="pos-cart-item-price">KSh {{ fNum(item.price) }} × {{ item.qty }}</div>
          </div>
          <div class="pos-cart-item-actions">
            <button class="pos-qty-btn" @click="changeQty(idx,-1)">−</button>
            <span class="pos-qty">{{ item.qty }}</span>
            <button class="pos-qty-btn" @click="changeQty(idx,1)">+</button>
            <button class="pos-remove-btn" @click="removeFromCart(idx)">✕</button>
          </div>
          <div class="pos-cart-item-total">KSh {{ fNum(item.price * item.qty) }}</div>
        </div>
      </div>

      <!-- Custom Entry -->
      <div class="pos-custom" v-if="showCustom">
        <input class="st-input mb-2" v-model="customName" placeholder="Item name" style="font-size:13px;" />
        <div class="d-flex gap-2">
          <input class="st-input" v-model.number="customPrice" type="number" placeholder="Price" style="font-size:13px;flex:1;" />
          <button class="st-btn st-btn-primary" @click="addCustom" style="font-size:13px;">Add</button>
        </div>
      </div>
      <button class="pos-custom-toggle" @click="showCustom=!showCustom">{{ showCustom ? '▲ Hide' : '+ Custom Item' }}</button>

      <!-- Totals -->
      <div class="pos-totals" v-if="cart.length">
        <div class="pos-total-row"><span>Subtotal</span><span>KSh {{ fNum(cartTotal) }}</span></div>
        <div class="pos-total-row pos-total-grand"><span>TOTAL</span><span>KSh {{ fNum(cartTotal) }}</span></div>
      </div>

      <!-- Payment Buttons -->
      <div class="pos-pay-section" v-if="cart.length">
        <button class="pos-pay-btn pos-pay-wallet" @click="payMethod='wallet';showPayModal=true" :disabled="processing">
          <span class="pos-pay-icon">💳</span> Student Wallet
        </button>
        <button class="pos-pay-btn pos-pay-mpesa" @click="payMethod='mpesa';showPayModal=true" :disabled="processing">
          <span class="pos-pay-icon">📱</span> M-Pesa
        </button>
        <button class="pos-pay-btn pos-pay-cash" @click="payMethod='cash';showPayModal=true" v-if="cashEnabled" :disabled="processing">
          <span class="pos-pay-icon">💵</span> Cash
        </button>
      </div>
    </div>

    <!-- Payment Modal -->
    <div class="st-modal-overlay" v-if="showPayModal" @click.self="showPayModal=false">
      <div class="st-modal" style="max-width:450px;">
        <div class="st-modal-header">
          <h5>{{ payMethodLabel }} — KSh {{ fNum(cartTotal) }}</h5>
          <button class="st-modal-close" @click="closePayModal">&times;</button>
        </div>
        <div class="st-modal-body">

          <!-- WALLET PAYMENT -->
          <div v-if="payMethod==='wallet'">
            <label class="st-label">Search Student</label>
            <input class="st-input mb-2" v-model="studentSearch" placeholder="Type student name or admission no..." @input="searchStudentDebounced" />
            <div v-if="searchingStudent" class="text-center py-2"><div class="spinner-border spinner-border-sm text-info"></div></div>
            <div v-if="studentResults.length" class="pos-student-list">
              <div class="pos-student-item" v-for="s in studentResults" :key="s.id" @click="selectStudent(s)" :class="{'pos-student-selected':selectedStudent&&selectedStudent.id===s.id}">
                <img v-if="s.photo_url" :src="s.photo_url" class="pos-student-photo" />
                <div v-else class="pos-student-photo-placeholder">👤</div>
                <div>
                  <div style="font-weight:600;color:#E8EDF4;">{{ s.name }}</div>
                  <div style="font-size:12px;color:#8BA4C4;">{{ s.admission_no }} — {{ s.class_name||'' }}</div>
                </div>
              </div>
            </div>
            <div v-if="selectedStudent" class="pos-student-selected-card mt-3">
              <div class="d-flex align-items-center">
                <img v-if="selectedStudent.photo_url" :src="selectedStudent.photo_url" class="pos-student-photo-lg" />
                <div v-else class="pos-student-photo-lg-placeholder">👤</div>
                <div style="margin-left:12px;">
                  <div style="font-weight:700;font-size:16px;color:#E8EDF4;">{{ selectedStudent.name }}</div>
                  <div style="font-size:13px;color:#8BA4C4;">{{ selectedStudent.admission_no }}</div>
                  <div style="font-size:18px;font-weight:800;margin-top:4px;" :style="{color:canAffordCart?'#10B981':'#EF4444'}">
                    Balance: KSh {{ fNum(walletBalance) }}
                  </div>
                  <div v-if="isCreditWallet" style="font-size:11px;color:#F59E0B;font-weight:600;margin-top:2px;">
                    🎓 Sponsored — Available credit: KSh {{ fNum(spendableAmount) }} of {{ fNum(creditLimit) }}
                  </div>
                </div>
              </div>
              <div v-if="!canAffordCart" class="pos-insufficient mt-2">
                ⚠️ {{ isCreditWallet ? 'Sponsor credit limit reached.' : 'Insufficient funds.' }} Short by KSh {{ fNum(cartTotal - spendableAmount) }}
              </div>

              <!-- PIN / OTP Verification -->
              <div v-if="canAffordCart" class="pos-verify mt-3">
                <div class="pos-verify-label">Verify Student Identity</div>
                <div class="pos-verify-tabs">
                  <button class="pos-verify-tab" :class="{active:verifyMethod==='pin'}" @click="verifyMethod='pin'">🔒 Wallet PIN</button>
                  <button class="pos-verify-tab" :class="{active:verifyMethod==='otp'}" @click="verifyMethod='otp'">📱 Send OTP</button>
                </div>

                <div v-if="verifyMethod==='pin'" class="mt-2">
                  <label class="st-label">Student enters their PIN</label>
                  <input class="st-input" v-model="walletPin" type="password" placeholder="Enter 4-digit PIN" maxlength="6" style="text-align:center;font-size:20px;letter-spacing:8px;font-weight:700;" />
                </div>

                <div v-if="verifyMethod==='otp'" class="mt-2">
                  <div v-if="!otpSent">
                    <p style="color:#8BA4C4;font-size:13px;">Send a one-time code to the student's parent phone.</p>
                    <button class="st-btn st-btn-primary" @click="sendOtp" :disabled="sendingOtp" style="width:100%;">
                      {{ sendingOtp ? 'Sending...' : '📱 Send OTP to Parent' }}
                    </button>
                  </div>
                  <div v-else>
                    <p style="color:#10B981;font-size:13px;">✅ OTP sent to {{ otpPhone }}. Ask student/cashier to enter it.</p>
                    <input class="st-input" v-model="otpCode" type="text" placeholder="Enter OTP code" maxlength="6" style="text-align:center;font-size:20px;letter-spacing:8px;font-weight:700;" />
                    <button class="pos-resend-btn" @click="sendOtp" :disabled="sendingOtp">Resend OTP</button>
                  </div>
                </div>

                <div v-if="verifyError" class="pos-error mt-2">{{ verifyError }}</div>
              </div>
            </div>
          </div>

          <!-- MPESA PAYMENT -->
          <div v-if="payMethod==='mpesa'">
            <label class="st-label">M-Pesa Phone Number</label>
            <input class="st-input mb-2" v-model="mpesaPhone" placeholder="e.g. 0712345678" />
            <label class="st-label">Customer Name (optional)</label>
            <input class="st-input mb-2" v-model="mpesaName" placeholder="e.g. John Doe" />
            <div v-if="paybills.length > 1" class="mb-2">
              <label class="st-label">Paybill / Till</label>
              <select class="st-input" v-model="selectedPaybillId" style="padding:9px 10px;">
                <option v-for="pb in paybills" :key="pb.id" :value="pb.id">{{ pb.shortcode }} — {{ pb.label }}</option>
              </select>
            </div>
            <p style="color:#8BA4C4;font-size:12px;">An STK push will be sent to this number. Customer confirms on their phone.</p>
          </div>

          <!-- CASH PAYMENT -->
          <div v-if="payMethod==='cash'">
            <label class="st-label">Amount Received (KSh)</label>
            <input class="st-input mb-2" v-model.number="cashReceived" type="number" :placeholder="''+cartTotal" style="font-size:20px;font-weight:700;text-align:center;" />
            <div v-if="cashReceived>=cartTotal" class="pos-change">
              <span>Change:</span> <strong>KSh {{ fNum(cashReceived - cartTotal) }}</strong>
            </div>
            <div v-else-if="cashReceived>0" class="pos-insufficient">
              ⚠️ Amount insufficient. Still need KSh {{ fNum(cartTotal - cashReceived) }}
            </div>
          </div>

          <!-- Order Summary -->
          <div class="pos-order-summary mt-3">
            <div class="pos-summary-title">Order Summary</div>
            <div class="pos-summary-item" v-for="item in cart" :key="item.name">
              <span>{{ item.qty }}× {{ item.name }}</span>
              <span>KSh {{ fNum(item.price * item.qty) }}</span>
            </div>
            <div class="pos-summary-total"><span>TOTAL</span><span>KSh {{ fNum(cartTotal) }}</span></div>
          </div>

          <!-- Status -->
          <div v-if="payError" class="pos-error mt-2">{{ payError }}</div>
          <div v-if="paySuccess" class="pos-success mt-2">{{ paySuccess }}</div>
        </div>
        <div class="st-modal-footer">
          <button class="st-btn st-btn-outline" @click="closePayModal">Cancel</button>
          <button class="st-btn st-btn-primary" @click="processPayment" :disabled="!canPay||processing" style="min-width:180px;">
            {{ processing ? 'Processing...' : 'Confirm Payment' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Receipt Modal -->
    <div class="st-modal-overlay" v-if="showReceipt" @click.self="showReceipt=false">
      <div class="st-modal" style="max-width:380px;">
        <div class="st-modal-header" style="background:#10B981;"><h5 style="color:#fff;">✅ Payment Successful</h5></div>
        <div class="st-modal-body" style="text-align:center;">
          <div style="font-size:32px;font-weight:800;color:#10B981;margin:12px 0;">KSh {{ fNum(receiptData.amount) }}</div>
          <div style="font-size:14px;color:#8BA4C4;">{{ receiptData.method }}</div>
          <div style="font-size:13px;color:#E8EDF4;margin-top:8px;" v-if="receiptData.customer">{{ receiptData.customer }}</div>
          <div style="font-size:12px;color:#8BA4C4;margin-top:4px;" v-if="receiptData.receipt_no">Receipt: {{ receiptData.receipt_no }}</div>
          <div style="font-size:12px;color:#8BA4C4;" v-if="receiptData.balance_after!==undefined">Wallet Balance: KSh {{ fNum(receiptData.balance_after) }}</div>
          <div style="font-size:12px;color:#8BA4C4;" v-if="receiptData.change>0">Change: KSh {{ fNum(receiptData.change) }}</div>
          <hr style="border-color:#1B3A6A;" />
          <div class="pos-summary-item" v-for="item in receiptData.items" :key="item.name" style="font-size:13px;">
            <span>{{ item.qty }}× {{ item.name }}</span><span>KSh {{ fNum(item.price * item.qty) }}</span>
          </div>
        </div>
        <div class="st-modal-footer" style="justify-content:center;gap:10px;">
          <button class="st-btn st-btn-outline" @click="printReceipt">🖨️ Print Receipt</button>
          <button class="st-btn st-btn-primary" @click="newOrder" style="min-width:160px;">New Order</button>
        </div>
      </div>
    </div>

    <!-- Hidden Thermal Receipt (80mm / 58mm) -->
    <div id="thermalReceipt" class="thermal-receipt" style="display:none;">
      <div style="text-align:center;font-weight:bold;font-size:16px;">{{ institutionName }}</div>
      <div style="text-align:center;font-size:11px;">POS Receipt</div>
      <div style="border-top:1px dashed #000;margin:6px 0;"></div>
      <div style="font-size:11px;">Receipt: {{ receiptData.receipt_no }}</div>
      <div style="font-size:11px;">Date: {{ new Date().toLocaleString() }}</div>
      <div style="font-size:11px;" v-if="receiptData.customer">Customer: {{ receiptData.customer }}</div>
      <div style="font-size:11px;">Payment: {{ receiptData.method }}</div>
      <div style="border-top:1px dashed #000;margin:6px 0;"></div>
      <table style="width:100%;font-size:11px;border-collapse:collapse;">
        <tr v-for="item in receiptData.items" :key="item.name">
          <td style="padding:2px 0;">{{ item.qty }}x {{ item.name }}</td>
          <td style="padding:2px 0;text-align:right;">{{ item.price * item.qty }}</td>
        </tr>
      </table>
      <div style="border-top:1px dashed #000;margin:6px 0;"></div>
      <div style="font-size:14px;font-weight:bold;text-align:right;">TOTAL: KSh {{ receiptData.amount }}</div>
      <div style="font-size:11px;text-align:right;" v-if="receiptData.balance_after!==undefined">Wallet Balance: KSh {{ receiptData.balance_after }}</div>
      <div style="font-size:11px;text-align:right;" v-if="receiptData.change>0">Change: KSh {{ receiptData.change }}</div>
      <div style="border-top:1px dashed #000;margin:6px 0;"></div>
      <div style="text-align:center;font-size:10px;">Powered by Simi Trac</div>
      <div style="text-align:center;font-size:10px;">simitrac.com</div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { executeJson, simitrack } from "@/api";
export default {
  name: 'POSTerminal',
  data() {
    return {
      items: [], categories: [], loading: true,
      activeCat: '', menuSearch: '',
      cart: [], showCustom: false, customName: '', customPrice: '',
      cashEnabled: true,

      // Payment
      showPayModal: false, payMethod: '', processing: false,
      payError: '', paySuccess: '',

      // Wallet
      studentSearch: '', studentResults: [], searchingStudent: false,
      selectedStudent: null, walletBalance: 0, walletType: 'prepaid', creditLimit: 0, searchTimer: null,
      verifyMethod: 'pin', walletPin: '', otpCode: '', otpSent: false, otpPhone: '', sendingOtp: false, verifyError: '',

      // M-Pesa
      mpesaPhone: '', mpesaName: '', paybills: [], selectedPaybillId: '',

      // Cash
      cashReceived: '',

      // Receipt
      showReceipt: false, receiptData: {},
      institutionName: ''
    };
  },
  computed: {
    filteredMenu() {
      let list = this.items.filter(i => i.is_available && i.is_available !== '0');
      if (this.activeCat) list = list.filter(i => i.category_id == this.activeCat);
      if (this.menuSearch) {
        const q = this.menuSearch.toLowerCase();
        list = list.filter(i => i.name.toLowerCase().includes(q));
      }
      return list.sort((a,b) => (a.sort_order||0) - (b.sort_order||0));
    },
    cartTotal() { return this.cart.reduce((s, i) => s + (i.price * i.qty), 0); },
    isCreditWallet() { return this.walletType === 'credit'; },
    spendableAmount() {
      // Credit wallets can go negative up to -credit_limit; prepaid wallets just use balance
      return this.isCreditWallet ? (this.walletBalance + this.creditLimit) : this.walletBalance;
    },
    canAffordCart() { return this.spendableAmount >= this.cartTotal; },
    payMethodLabel() {
      return { wallet: '💳 Student Wallet', mpesa: '📱 M-Pesa', cash: '💵 Cash' }[this.payMethod] || '';
    },
    canPay() {
      if (this.payMethod === 'wallet') {
        if (!this.selectedStudent || !this.canAffordCart) return false;
        if (this.verifyMethod === 'pin' && this.walletPin.length < 4) return false;
        if (this.verifyMethod === 'otp' && this.otpCode.length < 4) return false;
        return true;
      }
      if (this.payMethod === 'mpesa') return this.mpesaPhone.length >= 10;
      if (this.payMethod === 'cash') return this.cashReceived >= this.cartTotal;
      return false;
    }
  },
  mounted() { this.loadMenu(); this.loadPaybills(); this.institutionName = sessionStorage.getItem('institutionName') || 'Simi Trac'; },
  methods: {
    async loadMenu() {
      this.loading = true;
      try {
        const [itemsRes, catsRes] = await Promise.all([this.api('getMenuItems'), this.api('getMenuCategories')]);
        this.items = itemsRes.data || [];
        this.categories = catsRes.data || [];
        // Check if cash is enabled in settings
        const settings = await this.api('getSettings');
        if (settings.data) this.cashEnabled = (settings.data.pos_cash_enabled || '1') === '1';
      } catch(e) { console.error(e); }
      this.loading = false;
    },
    async loadPaybills() {
      try {
        const res = await this.api('getSettings');
        if (res.data?.paybills) {
          this.paybills = res.data.paybills;
          if (this.paybills.length > 0 && !this.selectedPaybillId) {
            const primary = this.paybills.find(p => p.isPrimary);
            this.selectedPaybillId = primary ? primary.id : this.paybills[0].id;
          }
        }
      } catch(e) {}
    },
    addToCart(item) {
      const existing = this.cart.find(c => c.id === item.id);
      if (existing) { existing.qty++; }
      else { this.cart.push({ id: item.id, name: item.name, price: parseFloat(item.price), qty: 1 }); }
    },
    addCustom() {
      if (!this.customName || !this.customPrice) return;
      this.cart.push({ id: 'custom_' + Date.now(), name: this.customName, price: this.customPrice, qty: 1 });
      this.customName = ''; this.customPrice = '';
    },
    changeQty(idx, delta) {
      this.cart[idx].qty += delta;
      if (this.cart[idx].qty <= 0) this.cart.splice(idx, 1);
    },
    removeFromCart(idx) { this.cart.splice(idx, 1); },
    clearCart() { if (confirm('Clear all items?')) this.cart = []; },

    // Student search with debounce
    searchStudentDebounced() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.searchStudent(), 400);
    },
    async searchStudent() {
      if (this.studentSearch.length < 2) { this.studentResults = []; return; }
      this.searchingStudent = true;
      try {
        const res = await this.api('searchStudents', { query: this.studentSearch });
        this.studentResults = res.data || [];
      } catch(e) { console.error(e); }
      this.searchingStudent = false;
    },
    async selectStudent(s) {
      this.selectedStudent = s;
      this.studentResults = [];
      this.studentSearch = s.name;
      // Get wallet balance — try rfidUid first (same as NFC), then walletId
      try {
        let res;
        if (s.rfid_uid) {
          res = await this.api('getWalletBalance', { rfidUid: s.rfid_uid });
        } else if (s.wallet_id) {
          res = await this.api('getWalletBalance', { walletId: s.wallet_id });
        } else {
          this.walletBalance = 0;
          return;
        }
        if (res.data && res.data.balance !== undefined) {
          this.walletBalance = parseFloat(res.data.balance || 0);
          this.walletType = res.data.wallet_type || 'prepaid';
          this.creditLimit = parseFloat(res.data.credit_limit || 0);
        } else if (res.data && res.data.wallet) {
          this.walletBalance = parseFloat(res.data.wallet.balance || 0);
          this.walletType = res.data.wallet.wallet_type || 'prepaid';
          this.creditLimit = parseFloat(res.data.wallet.credit_limit || 0);
        } else {
          this.walletBalance = 0; this.walletType = 'prepaid'; this.creditLimit = 0;
        }
      } catch(e) { this.walletBalance = 0; this.walletType = 'prepaid'; this.creditLimit = 0; }
    },

    async sendOtp() {
      if (!this.selectedStudent) return;
      this.sendingOtp = true;
      this.verifyError = '';
      try {
        const res = await this.api('sendPosOtp', { studentId: this.selectedStudent.id });
        if (res.success) {
          this.otpSent = true;
          this.otpPhone = res.data?.phone_masked || 'parent phone';
        } else {
          this.verifyError = res.message || 'Failed to send OTP';
        }
      } catch(e) { this.verifyError = 'Network error'; }
      this.sendingOtp = false;
    },

    async processPayment() {
      this.payError = ''; this.paySuccess = '';
      this.processing = true;

      const description = this.cart.map(i => `${i.qty}x ${i.name}`).join(', ');
      const itemsJson = JSON.stringify(this.cart.map(i => ({ name: i.name, qty: i.qty, price: i.price, menu_item_id: i.id || null })));

      try {
        if (this.payMethod === 'wallet') {
          // Include PIN or OTP for verification
          const verifyParams = {};
          if (this.verifyMethod === 'pin') verifyParams.walletPin = this.walletPin;
          else verifyParams.otpCode = this.otpCode;
          // Use posPayment endpoint
          const res = await this.api('posPayment', {
            rfidUid: this.selectedStudent.rfid_uid || '',
            studentId: this.selectedStudent.id,
            amount: this.cartTotal,
            description: description,
            itemsJson: itemsJson,
            tapPointId: sessionStorage.getItem('tapPointId') || '1',
            ...verifyParams
          });
          if (res.success) {
            this.receiptData = {
              amount: this.cartTotal, method: 'Student Wallet',
              customer: this.selectedStudent.name,
              receipt_no: res.data?.receipt_no || '',
              balance_after: res.data?.balance_after,
              items: [...this.cart]
            };
            this.showPayModal = false;
            this.showReceipt = true;
          } else {
            this.payError = res.message || 'Payment failed';
          }
        } else if (this.payMethod === 'mpesa') {
          const res = await this.api('posPaymentMpesa', {
            phone: this.mpesaPhone,
            customerName: this.mpesaName,
            amount: this.cartTotal,
            description: description,
            itemsJson: itemsJson,
            paybillId: this.selectedPaybillId
          });
          if (res.success) {
            const checkoutId = res.data?.checkout_request_id;
            this.paySuccess = '📱 M-Pesa prompt sent! Waiting for PIN entry...';
            this.processing = false;

            // Poll for STK result
            if (checkoutId) {
              let attempts = 0;
              const poll = setInterval(async () => {
                attempts++;
                try {
                  const r = await this.api('checkStkStatus', { checkoutRequestId: checkoutId });
                  const status = r.data?.status;
                  if (status === 'completed') {
                    this.paySuccess = r.message;
                    this.receiptData = {
                      amount: this.cartTotal, method: 'M-Pesa — ' + this.mpesaPhone,
                      customer: this.mpesaName || this.mpesaPhone,
                      receipt_no: r.data?.provider_ref || res.data?.receipt_no || '', items: [...this.cart]
                    };
                    clearInterval(poll);
                    setTimeout(() => { this.showPayModal = false; this.showReceipt = true; }, 1500);
                  } else if (status === 'failed' || status === 'cancelled') {
                    this.payError = r.message;
                    this.paySuccess = '';
                    clearInterval(poll);
                  } else if (attempts >= 20) {
                    this.paySuccess = '⏳ No response yet. Payment may still process.';
                    clearInterval(poll);
                  } else {
                    this.paySuccess = `⏳ Waiting for M-Pesa... (${attempts * 3}s)`;
                  }
                } catch(e) {}
              }, 3000);
            }
          } else {
            this.payError = res.message || 'M-Pesa request failed';
          }
        } else if (this.payMethod === 'cash') {
          const res = await this.api('posPaymentCash', {
            amount: this.cartTotal,
            cashReceived: this.cashReceived,
            description: description,
            customerName: '',
            itemsJson: itemsJson
          });
          if (res.success) {
            this.receiptData = {
              amount: this.cartTotal, method: 'Cash',
              customer: 'Walk-in', change: this.cashReceived - this.cartTotal,
              receipt_no: res.data?.receipt_no || '', items: [...this.cart]
            };
            this.showPayModal = false;
            this.showReceipt = true;
          } else {
            this.payError = res.message || 'Recording failed';
          }
        }
      } catch(e) { this.payError = 'Network error: ' + e.message; }
      this.processing = false;
    },

    printReceipt() {
      const el = document.getElementById('thermalReceipt');
      if (!el) return;
      el.style.display = 'block';
      const printWindow = window.open('', '_blank', 'width=300,height=600');
      printWindow.document.write(`
        <html><head><title>Receipt</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          body { font-family: 'Courier New', monospace; width: 72mm; margin: 4mm; padding: 0; font-size: 12px; color: #000; }
          table { width: 100%; }
          td { padding: 1px 0; }
        </style></head><body>${el.innerHTML}</body></html>
      `);
      printWindow.document.close();
      printWindow.onload = function() {
        printWindow.print();
        printWindow.close();
      };
      el.style.display = 'none';
    },

    closePayModal() {
      this.showPayModal = false;
      this.payError = ''; this.paySuccess = '';
      this.studentSearch = ''; this.studentResults = []; this.selectedStudent = null;
      this.walletPin = ''; this.otpCode = ''; this.otpSent = false; this.verifyError = ''; this.verifyMethod = 'pin';
      this.mpesaPhone = ''; this.mpesaName = ''; this.cashReceived = '';
    },
    newOrder() {
      this.showReceipt = false;
      this.cart = [];
      this.closePayModal();
    },

    getCatName(id) { return (this.categories.find(c=>c.id==id)||{}).name || ''; },
    fNum(n) { return Number(n||0).toLocaleString(); },
    async api(fn, params={}) {
      try {
        const res = await executeJson({ function: fn, ...params }, simitrack);
        return res.data;
      } catch(e) {
        return { success: false, message: e.message || 'Request failed', data: null };
      }
    }
  }
};
</script>

<style scoped>
.pos-container { display:flex; height:calc(100vh - 56px); overflow:hidden; background:#0A1628; }

/* LEFT PANEL — Menu */
.pos-menu { flex:1; display:flex; flex-direction:column; overflow:hidden; padding:12px; }
.pos-cats { display:flex; gap:6px; overflow-x:auto; padding-bottom:8px; flex-shrink:0; }
.pos-cat { padding:6px 14px; border-radius:20px; background:#0F2847; border:1px solid #1B3A6A; color:#8BA4C4; font-size:13px; cursor:pointer; white-space:nowrap; transition:.2s; }
.pos-cat.active { background:#2E86DE; color:#fff; border-color:#2E86DE; }
.pos-search { flex-shrink:0; margin-bottom:8px; }
.pos-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(130px, 1fr)); gap:8px; overflow-y:auto; flex:1; padding-bottom:12px; align-content:start; }
.pos-item { background:#0F2847; border:1px solid #1B3A6A; border-radius:10px; padding:12px 10px; cursor:pointer; transition:.15s; text-align:center; height:fit-content; max-height:110px; }
.pos-item:hover { border-color:#10B981; background:#102D52; transform:scale(1.02); }
.pos-item:active { transform:scale(0.97); }
.pos-item-out { opacity:0.3; pointer-events:none; }
.pos-item-price { font-size:18px; font-weight:800; color:#10B981; }
.pos-item-name { font-size:13px; color:#E8EDF4; font-weight:600; margin-top:4px; line-height:1.2; }
.pos-item-cat { font-size:10px; color:#4FC3F7; margin-top:3px; text-transform:uppercase; }
.pos-empty { grid-column:1/-1; text-align:center; padding:40px; color:#8BA4C4; }

/* RIGHT PANEL — Cart */
.pos-cart-panel { width:360px; background:#0A1B36; border-left:1px solid #1B3A6A; display:flex; flex-direction:column; }
.pos-cart-header { display:flex; justify-content:space-between; align-items:center; padding:14px 16px; border-bottom:1px solid #1B3A6A; }
.pos-clear-btn { background:none; border:1px solid #EF4444; color:#EF4444; border-radius:6px; padding:4px 10px; font-size:12px; cursor:pointer; }
.pos-cart-items { flex:1; overflow-y:auto; padding:8px 12px; }
.pos-cart-empty { text-align:center; color:#8BA4C4; padding:40px 16px; font-size:14px; }
.pos-cart-item { background:#0F2847; border-radius:8px; padding:10px 12px; margin-bottom:6px; }
.pos-cart-item-info { display:flex; justify-content:space-between; align-items:center; }
.pos-cart-item-name { font-size:14px; font-weight:600; color:#E8EDF4; }
.pos-cart-item-price { font-size:11px; color:#8BA4C4; }
.pos-cart-item-actions { display:flex; align-items:center; gap:6px; margin-top:6px; }
.pos-qty-btn { width:28px; height:28px; border-radius:6px; border:1px solid #1B3A6A; background:#0A1B36; color:#E8EDF4; font-size:16px; cursor:pointer; display:flex; align-items:center; justify-content:center; }
.pos-qty-btn:hover { border-color:#2E86DE; }
.pos-qty { color:#E8EDF4; font-weight:700; font-size:14px; min-width:20px; text-align:center; }
.pos-remove-btn { background:none; border:none; color:#EF4444; font-size:14px; cursor:pointer; margin-left:auto; }
.pos-cart-item-total { text-align:right; font-weight:700; font-size:15px; color:#10B981; margin-top:4px; }

/* Custom Entry */
.pos-custom { padding:8px 12px; border-top:1px solid #1B3A6A; }
.pos-custom-toggle { width:100%; background:none; border:none; color:#4FC3F7; font-size:12px; padding:6px; cursor:pointer; }

/* Totals */
.pos-totals { padding:12px 16px; border-top:1px solid #1B3A6A; }
.pos-total-row { display:flex; justify-content:space-between; color:#8BA4C4; font-size:13px; margin-bottom:4px; }
.pos-total-grand { font-size:20px; font-weight:800; color:#E8EDF4; margin-top:8px; }

/* Payment Buttons */
.pos-pay-section { padding:12px; display:flex; flex-direction:column; gap:8px; }
.pos-pay-btn { padding:14px; border-radius:10px; border:none; font-size:15px; font-weight:700; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:8px; transition:.15s; }
.pos-pay-btn:disabled { opacity:0.5; }
.pos-pay-wallet { background:#10B981; color:#fff; }
.pos-pay-wallet:hover:not(:disabled) { background:#0D9668; }
.pos-pay-mpesa { background:#4CAF50; color:#fff; }
.pos-pay-mpesa:hover:not(:disabled) { background:#388E3C; }
.pos-pay-cash { background:#F59E0B; color:#0A1B36; }
.pos-pay-cash:hover:not(:disabled) { background:#D97706; }
.pos-pay-icon { font-size:18px; }

/* Student search in modal */
.pos-student-list { max-height:200px; overflow-y:auto; }
.pos-student-item { display:flex; align-items:center; gap:10px; padding:8px 10px; border-radius:8px; cursor:pointer; transition:.15s; }
.pos-student-item:hover { background:#0F2847; }
.pos-student-selected { background:#0F2847; border:1px solid #2E86DE; }
.pos-student-photo { width:40px; height:40px; border-radius:8px; object-fit:cover; }
.pos-student-photo-placeholder { width:40px; height:40px; border-radius:8px; background:#1B3A6A; display:flex; align-items:center; justify-content:center; font-size:20px; }
.pos-student-selected-card { background:#0F2847; border:1px solid #1B3A6A; border-radius:10px; padding:14px; }
.pos-student-photo-lg { width:60px; height:60px; border-radius:10px; object-fit:cover; }
.pos-student-photo-lg-placeholder { width:60px; height:60px; border-radius:10px; background:#1B3A6A; display:flex; align-items:center; justify-content:center; font-size:28px; }
.pos-insufficient { background:#EF444422; color:#EF4444; padding:8px 12px; border-radius:8px; font-size:13px; font-weight:600; }
.pos-change { background:#10B98122; color:#10B981; padding:8px 12px; border-radius:8px; font-size:16px; text-align:center; }

/* Verification */
.pos-verify { background:#0A1B36; border:1px solid #1B3A6A; border-radius:10px; padding:14px; }
.pos-verify-label { font-size:12px; color:#8BA4C4; text-transform:uppercase; letter-spacing:0.3px; margin-bottom:10px; }
.pos-verify-tabs { display:flex; gap:6px; }
.pos-verify-tab { flex:1; padding:8px; border-radius:8px; background:#0F2847; border:1px solid #1B3A6A; color:#8BA4C4; font-size:13px; cursor:pointer; text-align:center; transition:.2s; }
.pos-verify-tab.active { background:#2E86DE; color:#fff; border-color:#2E86DE; }
.pos-verify-tab:hover:not(.active) { border-color:#2E86DE; }
.pos-resend-btn { background:none; border:none; color:#4FC3F7; font-size:12px; cursor:pointer; margin-top:6px; padding:0; }
.pos-resend-btn:hover { text-decoration:underline; }
.pos-resend-btn:disabled { color:#4A6A8A; }

/* Order summary */
.pos-order-summary { background:#0F2847; border-radius:8px; padding:12px; }
.pos-summary-title { font-size:12px; color:#8BA4C4; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:8px; }
.pos-summary-item { display:flex; justify-content:space-between; font-size:13px; color:#E8EDF4; padding:3px 0; }
.pos-summary-total { display:flex; justify-content:space-between; font-size:16px; font-weight:800; color:#10B981; border-top:1px solid #1B3A6A; margin-top:6px; padding-top:6px; }

.pos-error { background:#EF444422; color:#EF4444; padding:10px; border-radius:8px; font-size:13px; }
.pos-success { background:#10B98122; color:#10B981; padding:10px; border-radius:8px; font-size:13px; }

/* Modal — dark theme */
.st-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.65); z-index:200; display:flex; align-items:center; justify-content:center; padding:20px; }
.st-modal { background:#0A1B36; border:1px solid #1B3A6A; border-radius:14px; width:100%; overflow:hidden; box-shadow:0 20px 60px rgba(0,0,0,.5); }
.st-modal-header { display:flex; justify-content:space-between; align-items:center; padding:16px 20px; background:#0F2847; border-bottom:1px solid #1B3A6A; }
.st-modal-header h5 { margin:0; color:#E8EDF4; font-size:16px; font-weight:700; }
.st-modal-close { background:none; border:none; color:#8BA4C4; font-size:22px; cursor:pointer; padding:0 4px; }
.st-modal-close:hover { color:#EF4444; }
.st-modal-body { padding:20px; color:#E8EDF4; max-height:60vh; overflow-y:auto; }
.st-modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:14px 20px; border-top:1px solid #1B3A6A; }
.st-label { display:block; font-size:12px; color:#8BA4C4; margin-bottom:5px; text-transform:uppercase; letter-spacing:0.3px; }
.st-input { width:100%; padding:10px 14px; background:#0F2847; border:1px solid #1B3A6A; border-radius:8px; color:#E8EDF4; font-size:14px; outline:none; box-sizing:border-box; }
.st-input:focus { border-color:#2E86DE; }
.st-input::placeholder { color:#4A6A8A; }
.st-btn { padding:8px 18px; border-radius:8px; border:none; font-size:13px; font-weight:600; cursor:pointer; transition:.15s; }
.st-btn-primary { background:#2E86DE; color:#fff; }
.st-btn-primary:hover { background:#2574C4; }
.st-btn-primary:disabled { opacity:0.5; cursor:not-allowed; }
.st-btn-outline { background:transparent; border:1px solid #1B3A6A; color:#8BA4C4; }
.st-btn-outline:hover { border-color:#2E86DE; color:#E8EDF4; }

/* Responsive */
@media (max-width:768px) {
  .pos-container { flex-direction:column; }
  .pos-cart-panel { width:100%; height:50vh; border-left:none; border-top:1px solid #1B3A6A; }
  .pos-grid { grid-template-columns:repeat(auto-fill, minmax(100px, 1fr)); }
}
</style>